import React, { useState, useEffect } from 'react';
import './ProductGallery.css';

const ProductGallery = ({ images, productName }) => {
  const [currentIndex, setCurrentIndex] = useState(0);
  const [isLightboxOpen, setIsLightboxOpen] = useState(false);
  const [touchStart, setTouchStart] = useState(0);
  const [touchEnd, setTouchEnd] = useState(0);

  // Ensure we have at least one image
  const imageArray = images && images.length > 0 ? images : [{
    image_url: '/images/placeholder.jpg',
    alt_text: productName || 'Product image'
  }];

  // Handle keyboard navigation
  useEffect(() => {
    const handleKeyPress = (e) => {
      if (!isLightboxOpen) return;

      if (e.key === 'ArrowLeft') {
        handlePrevious();
      } else if (e.key === 'ArrowRight') {
        handleNext();
      } else if (e.key === 'Escape') {
        setIsLightboxOpen(false);
      }
    };

    window.addEventListener('keydown', handleKeyPress);
    return () => window.removeEventListener('keydown', handleKeyPress);
  }, [isLightboxOpen, currentIndex]);

  // Prevent body scroll when lightbox is open
  useEffect(() => {
    if (isLightboxOpen) {
      document.body.style.overflow = 'hidden';
    } else {
      document.body.style.overflow = 'unset';
    }
    return () => {
      document.body.style.overflow = 'unset';
    };
  }, [isLightboxOpen]);

  const handleNext = () => {
    setCurrentIndex((prev) => (prev + 1) % imageArray.length);
  };

  const handlePrevious = () => {
    setCurrentIndex((prev) => (prev - 1 + imageArray.length) % imageArray.length);
  };

  const handleThumbnailClick = (index) => {
    setCurrentIndex(index);
  };

  const handleTouchStart = (e) => {
    setTouchStart(e.targetTouches[0].clientX);
  };

  const handleTouchMove = (e) => {
    setTouchEnd(e.targetTouches[0].clientX);
  };

  const handleTouchEnd = () => {
    if (!touchStart || !touchEnd) return;

    const distance = touchStart - touchEnd;
    const minSwipeDistance = 50;

    if (distance > minSwipeDistance) {
      handleNext();
    } else if (distance < -minSwipeDistance) {
      handlePrevious();
    }

    setTouchStart(0);
    setTouchEnd(0);
  };

  const currentImage = imageArray[currentIndex];

  return (
    <>
      <div className="product-gallery">
        {/* Main Image Display */}
        <div
          className="gallery-main"
          onTouchStart={handleTouchStart}
          onTouchMove={handleTouchMove}
          onTouchEnd={handleTouchEnd}
        >
          <button
            className="gallery-zoom-btn"
            onClick={() => setIsLightboxOpen(true)}
            aria-label="Open full screen view"
            title="Click to zoom"
          >
            <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
              <circle cx="11" cy="11" r="7" />
              <line x1="16.5" y1="16.5" x2="21" y2="21" />
              <line x1="11" y1="8" x2="11" y2="14" />
              <line x1="8" y1="11" x2="14" y2="11" />
            </svg>
          </button>

          <img
            src={currentImage.image_url}
            alt={currentImage.alt_text || productName}
            className="gallery-main-image"
            onClick={() => setIsLightboxOpen(true)}
            loading="eager"
          />

          {imageArray.length > 1 && (
            <>
              <button
                className="gallery-nav gallery-nav-prev"
                onClick={handlePrevious}
                aria-label="Previous image"
              >
                ‹
              </button>
              <button
                className="gallery-nav gallery-nav-next"
                onClick={handleNext}
                aria-label="Next image"
              >
                ›
              </button>
              <div className="gallery-counter" aria-live="polite">
                {currentIndex + 1} / {imageArray.length}
              </div>
            </>
          )}
        </div>

        {/* Thumbnail Navigation */}
        {imageArray.length > 1 && (
          <div className="gallery-thumbnails" role="tablist" aria-label="Product images">
            {imageArray.map((img, index) => (
              <button
                key={index}
                className={`gallery-thumbnail ${index === currentIndex ? 'active' : ''}`}
                onClick={() => handleThumbnailClick(index)}
                role="tab"
                aria-selected={index === currentIndex}
                aria-label={`View image ${index + 1}`}
              >
                <img
                  src={img.image_url}
                  alt={img.alt_text || `${productName} view ${index + 1}`}
                  loading="lazy"
                />
              </button>
            ))}
          </div>
        )}
      </div>

      {/* Lightbox Modal */}
      {isLightboxOpen && (
        <div
          className="lightbox-overlay"
          onClick={() => setIsLightboxOpen(false)}
          role="dialog"
          aria-modal="true"
          aria-label="Full screen image view"
        >
          <button
            className="lightbox-close"
            onClick={() => setIsLightboxOpen(false)}
            aria-label="Close full screen view"
          >
            ✕
          </button>

          <div
            className="lightbox-content"
            onClick={(e) => e.stopPropagation()}
            onTouchStart={handleTouchStart}
            onTouchMove={handleTouchMove}
            onTouchEnd={handleTouchEnd}
          >
            <img
              src={currentImage.image_url}
              alt={currentImage.alt_text || productName}
              className="lightbox-image"
            />

            {imageArray.length > 1 && (
              <>
                <button
                  className="lightbox-nav lightbox-nav-prev"
                  onClick={handlePrevious}
                  aria-label="Previous image"
                >
                  ‹
                </button>
                <button
                  className="lightbox-nav lightbox-nav-next"
                  onClick={handleNext}
                  aria-label="Next image"
                >
                  ›
                </button>
                <div className="lightbox-counter">
                  {currentIndex + 1} / {imageArray.length}
                </div>
              </>
            )}
          </div>

          <p className="lightbox-hint">
            {imageArray.length > 1 && '← → to navigate | '}ESC to close
          </p>
        </div>
      )}
    </>
  );
};

export default ProductGallery;
